<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { RouterView } from 'vue-router'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import AppTopbar from '@/components/layout/AppTopbar.vue'
import MobileTabBar from '@/components/layout/MobileTabBar.vue'
import CreateSessionDialog from '@/modules/session-dashboard/components/CreateSessionDialog.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { useSessionListStore } from '@/modules/session-dashboard/stores/useSessionListStore'
import { useRouter } from 'vue-router'
import UpdateBanner from '@/components/UpdateBanner.vue'
import { useVersionChecker } from '@/lib/version'
import type { CreateSessionInput } from '@/modules/session-dashboard/types'

const showSidebar = ref(true)
const isMobile = useIsMobile()
const sessionStore = useSessionListStore()
const router = useRouter()

const { shouldShowBanner, isChunkError, startPolling, onVisibilityChange } = useVersionChecker()
let stopPolling: () => void

function handleCreate(input: CreateSessionInput) {
  router.push({
    name: 'session',
    state: {
      subject: input.subject,
      mode: input.mode,
      quantity: input.quantity,
      season: input.season,
    },
  })
}


onMounted(() => {
  stopPolling = startPolling()
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  stopPolling?.()
  document.removeEventListener('visibilitychange', onVisibilityChange)
})
</script>

<template>
  <div class="flex h-screen flex-col overflow-hidden">
    <UpdateBanner v-if="shouldShowBanner" :is-chunk-error="isChunkError" />

    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-background focus:rounded"
    >
      Skip to main content
    </a>

    <AppTopbar />

    <SidebarProvider class="relative min-h-0 h-full flex-1 overflow-hidden">
      <div
        class="hidden md:block overflow-hidden transition-[width] duration-200 ease-linear h-full"
        :style="{ width: showSidebar ? 'var(--sidebar-width, 16rem)' : '0' }"
      >
        <AppSidebar />
      </div>

      <SidebarInset class="overflow-hidden">
        <main id="main-content" class="flex-1 overflow-y-auto p-4 md:p-6 pb-16 md:pb-6">
          <div class="mx-auto w-full max-w-5xl">
            <RouterView />
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>

    <MobileTabBar v-if="isMobile" />

    <CreateSessionDialog v-model:open="sessionStore.isCreateDialogOpen" @submit="handleCreate" />
  </div>
</template>
